import classNames from 'classnames';
import { format, parse } from 'date-fns';

import BackIcon from '../assets/icons/back.svg?react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { RouteProps, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { CommonApi, PatientDTO, PeopleApi } from '../api';
import { PrivateRoute, Select } from '../components';
import { DoctorInnerPageLayout } from '../layouts';
import { normalizePhoneNumber, patientFormValidation } from '../utils';

export const DoctorEditPatientPage: FC<RouteProps> = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();

  const { data: patient, isLoading } = useQuery({
    queryKey: [PeopleApi.KEY, 'getPatientDetails', patientId],
    queryFn: () => PeopleApi.getPatientDetails(patientId!),
    enabled: !!patientId,
  });

  const { data: genderList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 107],
    queryFn: () => CommonApi.getReference(107),
  });

  const { mutate: updatePatient } = useMutation({
    mutationFn: (updatedData: PatientDTO) =>
      PeopleApi.updatePatient(patientId!, updatedData),
    onSuccess() {
      queryClient.refetchQueries([
        PeopleApi.KEY,
        'getPatientDetails',
        patientId,
      ]);
      navigate(-1);
    },
    onError(error) {
      console.error(error);
      toast.error('Ошибка! Проверьте данные');
    },
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<PatientDTO>({
    defaultValues: {},
  });

  useEffect(() => {
    if (patient) {
      reset({
        ...patient,
        gender_id: patient.gender_id,
        username: patient.phone,
        email:
          patient.contacts.find((contact) => contact.contact_type.id === 19)
            ?.value || '',
        birthdate: format(new Date(patient.birthdate), 'dd-MM-yyyy'),
      });
    }
  }, [patient, reset]);

  const onSubmit: SubmitHandler<PatientDTO> = useCallback(
    async (data) => {
      const updatedContacts = data.contacts.map((contact) => {
        if (contact.contact_type.id === 18) {
          return { ...contact, value: normalizePhoneNumber(data.username) };
        }
        if (contact.contact_type.id === 19) {
          return { ...contact, value: data.email };
        }
        return contact;
      });
      let formattedBirthdate = data.birthdate;
      if (data.birthdate?.trim()) {
        try {
          formattedBirthdate = format(
            parse(data.birthdate, 'dd-MM-yyyy', new Date()),
            'yyyy-MM-dd',
          );
        } catch {
          toast.error('Неверный формат даты');
          return;
        }
      }

      updatePatient({
        ...data,
        username: normalizePhoneNumber(data.username),
        birthdate: formattedBirthdate,
        contacts: updatedContacts,
      });
    },
    [updatePatient],
  );

  if (isLoading || !patientId) return <div>Загрузка...</div>;

  return (
    <PrivateRoute>
      <DoctorInnerPageLayout>
        <div className="grid grid-cols-2">
          <div className="rounded-[16px] bg-brand-6">
            <header className="p-[20px]">
              <button
                className="flex items-center gap-[6px] text-[20px] font-bold"
                onClick={() => navigate(-1)}
                type="button"
              >
                <BackIcon />
                Вернуться назад
              </button>
            </header>
            <form
              className="grid gap-[10px] p-[20px]"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div>
                <input
                  {...register('surname', patientFormValidation.surname)}
                  className={classNames(
                    'input',
                    errors.surname && 'input--invalid',
                  )}
                  placeholder="Фамилия"
                  type="text"
                />
                {errors.surname && (
                  <p className="text-red-500 mt-[10px] text-sm">
                    {errors.surname.message}
                  </p>
                )}
              </div>
              <div>
                <input
                  {...register('name', patientFormValidation.name)}
                  className={classNames(
                    'input',
                    errors.name && 'input--invalid',
                  )}
                  placeholder="Имя"
                  type="text"
                />
                {errors.name && (
                  <p className="text-red-500 mt-[10px] text-sm">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <input
                  {...register('midname', patientFormValidation.midname)}
                  className={classNames(
                    'input',
                    errors.midname && 'input--invalid',
                  )}
                  placeholder="Отчество"
                  type="text"
                />
                {errors.midname && (
                  <p className="text-red-500 mt-[10px] text-sm">
                    {errors.midname.message}
                  </p>
                )}
              </div>
              <div>
                <Controller
                  control={control}
                  name="gender_id"
                  rules={patientFormValidation.gender_id}
                  render={({ field, fieldState }) => (
                    <Select
                      error={fieldState.error}
                      onChange={field.onChange}
                      options={genderList.map((gender) => ({
                        label: gender.value,
                        value: gender.id,
                      }))}
                      placeholder="Пол"
                      value={field.value}
                    />
                  )}
                />
                {errors.gender_id && (
                  <p className="text-red-500 mt-[10px] text-sm">
                    {errors.gender_id.message}
                  </p>
                )}
              </div>
              <div>
                <Controller
                  control={control}
                  name="username"
                  rules={patientFormValidation.username}
                  render={({ field, fieldState }) => (
                    <InputMask
                      {...field}
                      alwaysShowMask
                      className={classNames(
                        'input',
                        fieldState.invalid && 'input--invalid',
                      )}
                      mask="+7 999 999 99 99"
                      maskPlaceholder={null}
                      type="tel"
                      value={field.value ?? ''}
                    />
                  )}
                />
                {errors.username && (
                  <p className="text-red-500 mt-[10px] text-sm">
                    {errors.username.message}
                  </p>
                )}
              </div>
              <div>
                <input
                  {...register('email', patientFormValidation.email)}
                  className={classNames(
                    'input',
                    errors.email && 'input--invalid',
                  )}
                  placeholder="Электронная почта"
                  type="email"
                />
                {errors.email && (
                  <p className="text-red-500 mt-[10px] text-sm">
                    {errors.email.message}
                  </p>
                )}
              </div>
              <div>
                <Controller
                  control={control}
                  name="birthdate"
                  rules={patientFormValidation.birthdate}
                  render={({ field, fieldState }) => (
                    <InputMask
                      {...field}
                      alwaysShowMask
                      className={classNames(
                        'input',
                        fieldState.invalid && 'input--invalid',
                      )}
                      mask="99-99-9999"
                      maskPlaceholder={null}
                      placeholder="Дата Рождения (ДД-ММ-ГГГГ)"
                      type="tel"
                      value={field.value ?? ''}
                    />
                  )}
                />
                {errors.birthdate && (
                  <p className="text-red-500 mt-[10px] text-sm">
                    {errors.birthdate.message}
                  </p>
                )}
              </div>
              <button
                className="button button--block button--primary mt-[20px]"
                type="submit"
              >
                Сохранить изменения
              </button>
            </form>
          </div>
        </div>
      </DoctorInnerPageLayout>
    </PrivateRoute>
  );
};
